import { useApiWorkspaceConfigsGetCollection } from '@r/api/generated/client.ts';
import type { GroupedWorkspaceConfigModel } from '@r/api/generated/client.schemas.ts';
import { updateFavicon } from '@r/utils/utils.ts';
import { useContext, useEffect } from 'react';
import { WorkspaceConfigContext } from '@r/context/workspace-config-context.ts';
import { maintenanceCriticalQueryOptions } from '@r/lib/maintenance-critical-query.ts';

const APP_ID: string = import.meta.env.VITE_APP_ID;

const applyWorkspaceStyle = (theme: GroupedWorkspaceConfigModel) => {
  if (theme?.groupedConfigs?.style) {
    for (const key in theme.groupedConfigs.style) {
      const value = theme.groupedConfigs.style[key];
      document.documentElement.style.setProperty(`--${key}`, `${value}`);
    }
  }
};

export const UseWorkspaceConfigs = (applyTheme = false) => {
  // Le back scope /workspaceConfigs via la session (header App-ID), pas via un id
  // de workspace en query param : une seule entrée de cache suffit, quel que soit
  // le workspace actif.
  const workSpaceConfigQuery = useApiWorkspaceConfigsGetCollection({
    query: {
      queryKey: ['workspaceConfigs'],
      staleTime: Number.POSITIVE_INFINITY,
      // 500 → retries puis page maintenance au boot (app.tsx)
      ...maintenanceCriticalQueryOptions
    },
    request: { headers: { 'App-ID': APP_ID } }
  });

  const { data, isSuccess } = workSpaceConfigQuery;

  useEffect(() => {
    if (!applyTheme || !isSuccess) return;

    if (data.groupedConfigs?.media?.favicon) {
      updateFavicon(data.groupedConfigs.media.favicon);
    }

    applyWorkspaceStyle(data);
  }, [applyTheme, isSuccess, data]);

  return workSpaceConfigQuery;
};

export const useGetConfigByType = (type: string, key: string, multi = false): string[] | null => {
  const workspaceConfigContext = useContext(WorkspaceConfigContext);
  const configs = workspaceConfigContext?.groupedConfigs?.[type];

  if (!configs) return null;

  if (multi) {
    const urls: string[] = [];
    for (const [configKey, value] of Object.entries(configs)) {
      if (configKey.startsWith(key) && value !== null) {
        urls.push(value as string);
      }
    }
    return urls.length > 0 ? urls : null;
  }

  return configs[key] ? [configs[key] as string] : null;
};
